import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo.ts';

/**
 * Changelog page. Signals active development to prospects and procurement teams. New entries
 * should be appended at the TOP so the most recent change is what loads above the fold.
 *
 * Convention:
 *   - Each entry has a version + date + 3-6 bullet points
 *   - Keep bullets concrete and user-facing (no "refactored DI container" level)
 *   - Tag with [Added] / [Improved] / [Fixed] / [Security] for scannability
 *   - Don't mention specific customers or revenue numbers — those go in case studies
 */

type ChangeKind = 'Added' | 'Improved' | 'Fixed' | 'Security';

type Entry = {
  version: string;
  date: string;
  highlights: string;
  changes: Array<{ kind: ChangeKind; text: string }>;
};

const ENTRIES: Entry[] = [
  {
    version: 'v1.0',
    date: '2026-05-26',
    highlights:
      'EAA Proof launches publicly. WCAG 2.1 AA scanning via axe-core, EU model ' +
      'accessibility statement generation, B2B VAT-compliant Stripe checkout, and a 7-day ' +
      'money-back guarantee.',
    changes: [
      { kind: 'Added', text: 'Single-scan (€29), Basic (€9/mo, 5 scans), Agency (€49/mo, 50 scans) plans with EU VAT auto-applied at checkout.' },
      { kind: 'Added', text: 'Real-Chromium scanning via Microsoft Playwright with multi-page same-origin crawl (up to 10 pages).' },
      { kind: 'Added', text: 'Plain-English remediation guidance generated by Anthropic Claude Sonnet, with 5 anti-hallucination guards.' },
      { kind: 'Added', text: 'EU model accessibility statement HTML (Implementing Decision (EU) 2018/1523), auto-filled from real scan results.' },
      { kind: 'Added', text: 'GDPR cookie consent banner with Google Consent Mode v2 wiring.' },
      { kind: 'Added', text: 'Imprint page (§5 TMG), refund policy, FAQ, and competitor comparison pages (vs AccessiBe / UserWay / Siteimprove).' },
      { kind: 'Security', text: 'HSTS, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy, and a strict Content-Security-Policy enabled on every response.' },
      { kind: 'Security', text: 'RFC 9116 security.txt published at /.well-known/security.txt for vulnerability disclosure.' },
    ],
  },
  // TODO: USER INPUT — append future entries at the TOP of this array as you ship.
  // Pattern: { version: 'v1.x', date: 'YYYY-MM-DD', highlights: '...', changes: [...] }
];

const KIND_CLASS: Record<ChangeKind, string> = {
  Added: 'changelog-tag-added',
  Improved: 'changelog-tag-improved',
  Fixed: 'changelog-tag-fixed',
  Security: 'changelog-tag-security',
};

export default function ChangelogPage() {
  useSeo({
    title: 'Changelog',
    canonicalPath: '/changelog',
    description:
      "What's new in EAA Proof. Track our shipped features, fixes, and security improvements.",
  });

  return (
    <div className="page changelog-page">
      <header>
        <h1>Changelog</h1>
        <p className="muted">
          Everything we ship, dated and tagged. Subscribe to{' '}
          <a href="https://eaaproof.com/changelog.rss" rel="alternate">RSS</a>{' '}
          {/* TODO: USER INPUT — wire up the RSS feed when ready, or remove this link. */}
          or follow us on{' '}
          <a href="https://twitter.com/eaaproof" target="_blank" rel="noopener noreferrer">
            Twitter
          </a>
          .{' '}
          {/* TODO: USER INPUT — replace the Twitter handle with your real one, or remove. */}
        </p>
      </header>

      <div className="changelog-list">
        {ENTRIES.map((entry) => (
          <article key={entry.version} className="changelog-entry">
            <header className="changelog-entry-head">
              <h2>{entry.version}</h2>
              <time dateTime={entry.date}>{entry.date}</time>
            </header>
            <p className="changelog-highlights">{entry.highlights}</p>
            <ul className="changelog-changes">
              {entry.changes.map((c, i) => (
                <li key={i}>
                  <span className={`changelog-tag ${KIND_CLASS[c.kind]}`}>{c.kind}</span>
                  <span>{c.text}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>

      <p className="changelog-footer muted small">
        Spotted a bug or have a feature request?{' '}
        <a href="mailto:support@eaaproof.com">Email support@eaaproof.com</a> —
        we read every message. For security issues, see our{' '}
        <a href="/.well-known/security.txt">disclosure policy</a>.{' '}
        Looking for the full audit trail? <Link to="/about">About EAA Proof</Link>.
      </p>
    </div>
  );
}
